import { matchPath } from 'react-router'

import { RouteName } from '@/router'

/**
 * restriction dot-notation prefix → TeacherFeatureKey 매핑 (SSOT)
 *
 * TeacherFeatureKey 타입은 이 맵에서 추출된다. 새 권한 추가 시 여기에만 작성.
 */
const RESTRICTION_FEATURE_MAP = {
  'mathflat.teacher.signature_workbook': 'CONTENT_SIGNATURE_WORKBOOK',
  'mathflat.teacher.academy_group_workbook': 'CONTENT_ACADEMY_GROUP_WORKBOOK',
  'mathflat.teacher.scienceflat': 'CONTENT_SCIENCEFLAT',
  'mathflat.teacher.englishflat': 'CONTENT_ENGLISHFLAT',
  // worksheet
  'mathflat.teacher.custom_worksheet.academy_group': 'WORKSHEET_ACADEMY_GROUP',
  'mathflat.teacher.custom_worksheet.academy.copy_to_academy_group': 'WORKSHEET_ACADEMY_COPY',
  'mathflat.teacher.custom_worksheet.academy.my_list': 'WORKSHEET_ACADEMY_MY_LIST',
  'mathflat.teacher.custom_worksheet.academy.create': 'WORKSHEET_ACADEMY_CREATE',
  'mathflat.teacher.custom_worksheet.academy.edit': 'WORKSHEET_ACADEMY_EDIT',
  'mathflat.teacher.custom_worksheet.academy.delete': 'WORKSHEET_ACADEMY_DELETE',
  // workbook
  'mathflat.teacher.custom_workbook.academy_group': 'WORKBOOK_ACADEMY_GROUP',
  'mathflat.teacher.custom_workbook.academy.copy_to_academy_group': 'WORKBOOK_ACADEMY_COPY',
  'mathflat.teacher.custom_workbook.academy.create': 'WORKBOOK_ACADEMY_CREATE',
  'mathflat.teacher.custom_workbook.academy.edit': 'WORKBOOK_ACADEMY_EDIT',
  'mathflat.teacher.custom_workbook.academy.delete': 'WORKBOOK_ACADEMY_DELETE',
  'mathflat.teacher.exam': 'EXAM',
  'mathflat.teacher.shared_worksheet': 'SHARED_WORKSHEET',
  'mathflat.teacher.theme_worksheet.kmm': 'THEME_WORKSHEET_KMM',
  'mathflat.teacher.theme_worksheet.entrance_test': 'THEME_WORKSHEET_ENTRANCE_TEST',
  'mathflat.teacher.theme_worksheet.general_test': 'THEME_WORKSHEET_GENERAL_TEST',
  'mathflat.teacher.cart.preview': 'CART_PREVIEW',
  'mathflat.teacher.order.preview': 'ORDER_PREVIEW',
  'mathflat.teacher.payment.preview': 'PAYMENT_PREVIEW',
  'mathflat.teacher.payment.read': 'PAYMENT_READ',
  'mathflat.teacher.study_report_legacy': 'STUDY_REPORT_LEGACY',
  'mathflat.teacher.cas': 'CAS',
  'mathflat.teacher.lesson.guest': 'LESSON_GUEST',
  'mathflat.teacher.daily_worksheet': 'DAILY_WORKSHEET',
  'mathflat.teacher.creverse_guest': 'CREVERSE_GUEST',
} as const satisfies Record<string, string>

export type TeacherFeatureKey =
  (typeof RESTRICTION_FEATURE_MAP)[keyof typeof RESTRICTION_FEATURE_MAP]

/** TeacherFeatureKey → restriction prefix 역참조 */
const FEATURE_RESTRICTION_MAP: Record<TeacherFeatureKey, string> = Object.fromEntries(
  Object.entries(RESTRICTION_FEATURE_MAP).map(([prefix, key]) => [key, prefix]),
) as Record<TeacherFeatureKey, string>

/**
 * FE SSOT: feature key → route 게이팅 설정
 */
const ROUTE_PERMISSION_MAP: Partial<Record<TeacherFeatureKey, ValueOf<typeof RouteName>[]>> = {
  CONTENT_SIGNATURE_WORKBOOK: [
    RouteName.signatureWorkbook,
    RouteName.makeWorksheetBySignatureWorkbook,
    RouteName.makeSignatureWorkbook,
    RouteName.makeSignatureWorkbookProductList,
    RouteName.makeSignatureWorkbookCover,
    RouteName.makeSignatureWorkbookCoverCreate,
    RouteName.makeSignatureWorkbookCoverEditOrCopyEdit,
    RouteName.signatureWorkbookViewer,
    RouteName.signatureWorkbookViewerByStudent,
  ],
  CONTENT_ACADEMY_GROUP_WORKBOOK: [
    RouteName.academyGroupWorkbook,
    RouteName.academyGroupWorkbookViewer,
    RouteName.academyGroupWorkbookViewerByStudent,
  ],
  // academyShareScopeWorksheet는 restriction 없음 — 항상 접근 가능
  WORKSHEET_ACADEMY_GROUP: [RouteName.academyGroupShareScopeWorksheet],
  EXAM: [
    RouteName.kmmRoot,
    RouteName.kmmOverview,
    RouteName.kmmGuideline,
    RouteName.kmmRegister,
    RouteName.kmmHighSchoolRegister,
    RouteName.kmmApplyMaterials,
    RouteName.weizmannExam,
    RouteName.examGroupScoring,
    RouteName.kmmAutoAssigningManage,
  ],
  SHARED_WORKSHEET: [RouteName.share, RouteName.shareWorksheetSetting],
  THEME_WORKSHEET_KMM: [RouteName.kmmExamWorksheet],
  THEME_WORKSHEET_ENTRANCE_TEST: [RouteName.themeEntranceTest],
  THEME_WORKSHEET_GENERAL_TEST: [RouteName.generalTest],
  STUDY_REPORT_LEGACY: [RouteName.createLessonReport, RouteName.updateLessonReport],
  PAYMENT_READ: [RouteName.myStatus],
  CAS: [
    RouteName.cas,
    RouteName.makeCas,
    RouteName.lessonCas,
    RouteName.lessonCasStudent,
    RouteName.lessonCasGrade,
    RouteName.lessonCasClass,
    RouteName.casGroupScoringByLessonClass,
    RouteName.casGroupScoringByGrade,
  ],
  CREVERSE_GUEST: [
    RouteName.lessonGuestEntranceTest,
    RouteName.lessonGuestEntranceTestWorksheet,
    RouteName.lessonGuestEntranceTestWorksheetStudent,
    RouteName.lessonGuestEntranceTestCas,
    RouteName.lessonGuestEntranceTestCasStudent,
  ],
  DAILY_WORKSHEET: [
    RouteName.lessonDailyLearning,
    RouteName.lessonDailyLearningLiveLesson,
    RouteName.lessonDailyLearningLiveLessonStudent,
    RouteName.studentTodayLearningManage,
  ],
}

/**
 * FE SSOT: feature key → workbook type 매핑
 */
const CONTENT_PERMISSION_MAP: Partial<Record<TeacherFeatureKey, string[]>> = {
  CONTENT_SIGNATURE_WORKBOOK: ['CUSTOM_SIGNATURE'],
  CONTENT_ACADEMY_GROUP_WORKBOOK: ['ACADEMY_GROUP'],
}

/**
 * restriction 패턴이 대상 경로에 매칭되는지 확인
 *
 * - "mathflat.teacher.signature-workbook.*" → prefix + 하위 모두 매칭
 * - "mathflat.teacher.signature-workbook.rent" → 정확히 일치만 매칭
 */
function matchRestriction(pattern: string, target: string): boolean {
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -2)
    return target === prefix || target.startsWith(prefix + '.')
  }
  return pattern === target
}

export class FeaturePermission {
  private readonly _restrictions: readonly string[]

  constructor(restrictions: string[]) {
    this._restrictions = restrictions
  }

  /** 대상 경로가 restriction에 매칭되는지 여부 */
  isRestricted(target: string): boolean {
    return this._restrictions.some((pattern) => matchRestriction(pattern, target))
  }

  /** TeacherFeatureKey가 허용되는지 여부. restriction에 없으면 허용 (blacklist) */
  isAllowed(key: TeacherFeatureKey): boolean {
    const prefix = FEATURE_RESTRICTION_MAP[key]
    if (!prefix) return true
    return !this.isRestricted(prefix)
  }

  /**
   * 주어진 workbook type이 허용되는지 여부.
   * CONTENT_PERMISSION_MAP에 매핑된 key가 restricted이면 false.
   */
  isWorkbookTypeAllowed(type: string): boolean {
    return (Object.entries(CONTENT_PERMISSION_MAP) as [TeacherFeatureKey, string[]][]).every(
      ([key, types]) => !types.includes(type) || this.isAllowed(key),
    )
  }

  /**
   * 주어진 pathname이 접근 허용되는지 여부.
   *
   * ROUTE_PERMISSION_MAP에 선언된 key 중 restricted 상태인 key의 route와
   * 매칭되면 false를 반환한다.
   */
  isRouteAllowed(pathname: string): boolean {
    return (
      Object.entries(ROUTE_PERMISSION_MAP) as [TeacherFeatureKey, ValueOf<typeof RouteName>[]][]
    ).every(
      ([key, routes]) =>
        !routes?.length ||
        this.isAllowed(key) ||
        !routes.some((route) => matchPath(pathname, { path: route, exact: true })),
    )
  }
}
